/**
 * GruposTestes.js - Gestão de Grupos de Testes Comportamentais
 * Permite criar grupos de trabalhadores e aplicar testes em lote
 * com dashboard comparativo de resultados
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { testGroupsAPI, psychologistsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Users,
  Plus,
  Trash2,
  Eye,
  BarChart3,
  Clock,
  CheckCircle,
  AlertCircle,
  Brain,
  FileText,
  Loader2,
  Send,
  RefreshCw,
  Download,
  ChevronRight,
  UserPlus,
  Copy,
  MessageSquare,
  Pencil,
  X
} from 'lucide-react';
import { toast } from 'sonner';

// Configuração dos tipos de teste
const TIPOS_TESTE = {
  disc: { label: 'DISC Tradicional', color: 'bg-violet-100 text-violet-700', icon: '🎯' },
  bigfive: { label: 'Big Five (OCEAN)', color: 'bg-blue-100 text-blue-700', icon: '🧠' },
  g2bp: { label: 'G2 Behavioral Profile', color: 'bg-indigo-100 text-indigo-700', icon: '⭐' }
};

// Utilitário para formatar CPF
const formatCPF = (cpf) => {
  if (!cpf) return '';
  const cleaned = cpf.replace(/\D/g, '');
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const cleanCPF = (cpf) => cpf?.replace(/\D/g, '') || '';

export default function GruposTestes() {
  const navigate = useNavigate();
  const { user, selectedCompany } = useAuth();
  
  // Estado principal
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [psychologists, setPsychologists] = useState([]);
  
  // Modal de criação
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    tipos_teste: [],
    validade_dias: 14,
    crp_obrigatorio: false,
    psicologo_id: ''
  });
  
  // Membros do grupo
  const [membros, setMembros] = useState([]);
  const [novoMembro, setNovoMembro] = useState({ cpf: '', nome: '', email: '', telefone: '', cargo: '' });
  
  // Modal de detalhes
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupDetail, setGroupDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Edição de membros
  const [showEditMembersModal, setShowEditMembersModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [newMemberForGroup, setNewMemberForGroup] = useState({ cpf: '', nome: '', email: '', telefone: '', cargo: '' });
  const [savingMember, setSavingMember] = useState(false);

  // Recarrega dados quando a empresa selecionada mudar
  useEffect(() => {
    setLoading(true);
    fetchGrupos();
    fetchPsychologists();
  }, [selectedCompany?.company_id]);

  const fetchGrupos = async () => {
    try {
      setLoading(true);
      const response = await testGroupsAPI.list();
      setGrupos(response.data);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
      toast.error('Erro ao carregar grupos');
    } finally {
      setLoading(false);
    }
  };

  const fetchPsychologists = async () => {
    try {
      const response = await psychologistsAPI.list();
      setPsychologists(response.data);
    } catch (error) {
      console.error('Erro ao carregar psicólogos:', error);
    }
  };

  // Adicionar membro
  const handleAddMembro = () => {
    const cpfLimpo = cleanCPF(novoMembro.cpf);
    
    if (!cpfLimpo || cpfLimpo.length !== 11) {
      toast.error('CPF inválido');
      return;
    }
    
    if (!novoMembro.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    
    // Verificar duplicado
    if (membros.some(m => cleanCPF(m.cpf) === cpfLimpo)) {
      toast.error('CPF já adicionado');
      return;
    }
    
    setMembros([...membros, { ...novoMembro, cpf: cpfLimpo }]);
    setNovoMembro({ cpf: '', nome: '', email: '', telefone: '', cargo: '' });
  };

  const handleRemoveMembro = (index) => {
    setMembros(membros.filter((_, i) => i !== index));
  };

  // Toggle tipo de teste
  const toggleTipoTeste = (tipo) => {
    setFormData(prev => ({
      ...prev,
      tipos_teste: prev.tipos_teste.includes(tipo)
        ? prev.tipos_teste.filter(t => t !== tipo)
        : [...prev.tipos_teste, tipo]
    }));
  };

  // Criar grupo
  const handleCreateGroup = async () => {
    if (!formData.nome.trim()) {
      toast.error('Nome do grupo é obrigatório');
      return;
    }
    
    if (formData.tipos_teste.length === 0) {
      toast.error('Selecione pelo menos um tipo de teste');
      return;
    }
    
    if (membros.length === 0) {
      toast.error('Adicione pelo menos um membro ao grupo');
      return;
    }
    
    setCreating(true);
    try {
      await testGroupsAPI.create({
        ...formData,
        membros: membros
      });
      
      toast.success(`Grupo criado! ${membros.length * formData.tipos_teste.length} convites enviados.`);
      setShowCreateModal(false);
      resetForm();
      fetchGrupos();
    } catch (error) {
      console.error('Erro ao criar grupo:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar grupo');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      descricao: '',
      tipos_teste: [],
      validade_dias: 14,
      crp_obrigatorio: false,
      psicologo_id: ''
    });
    setMembros([]);
    setNovoMembro({ cpf: '', nome: '', email: '', telefone: '', cargo: '' });
  };

  // Abrir detalhes do grupo
  const handleOpenDetail = async (group) => {
    setSelectedGroup(group);
    setLoadingDetail(true);
    try {
      const response = await testGroupsAPI.getById(group.id);
      setGroupDetail(response.data);
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
      toast.error('Erro ao carregar detalhes do grupo');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Excluir grupo
  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Tem certeza que deseja arquivar este grupo?')) return;
    
    try {
      await testGroupsAPI.delete(groupId);
      toast.success('Grupo arquivado');
      fetchGrupos();
      setSelectedGroup(null);
      setGroupDetail(null);
    } catch (error) {
      toast.error('Erro ao arquivar grupo');
    }
  };

  // Formatar data
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Normalizar telefone para formato internacional Brasil
  const normalizePhone = (phone) => {
    if (!phone) return null;
    // Remove tudo que não é número
    let cleaned = phone.replace(/\D/g, '');
    // Se já começa com 55, mantém
    if (cleaned.startsWith('55') && cleaned.length >= 12) {
      return cleaned;
    }
    // Se tem 10 ou 11 dígitos (DDD + número), adiciona 55
    if (cleaned.length >= 10 && cleaned.length <= 11) {
      return '55' + cleaned;
    }
    // Se tem 8 ou 9 dígitos (só número sem DDD), não tem como saber o DDD
    return cleaned.length >= 8 ? cleaned : null;
  };

  // Construir mensagem de convite para WhatsApp
  const buildWhatsAppMessage = (membro, grupo) => {
    // Obter testes pendentes e seus links
    const testesPendentes = [];
    const baseUrl = window.location.origin;
    
    if (membro.testes) {
      for (const [tipo, teste] of Object.entries(membro.testes)) {
        if (teste.status !== 'respondido' && teste.token) {
          testesPendentes.push({
            tipo,
            label: TIPOS_TESTE[tipo]?.label || tipo,
            link: `${baseUrl}/m/test-invite/${teste.token}`
          });
        }
      }
    }
    
    const tiposLabel = testesPendentes.length > 0
      ? testesPendentes.map(t => t.label).join(', ')
      : grupo.tipos_teste.map(t => TIPOS_TESTE[t]?.label || t).join(', ');
    
    // Construir lista de links
    const linksSection = testesPendentes.length > 0
      ? testesPendentes.map(t => `🔗 ${t.label}: ${t.link}`).join('\n')
      : '';
    
    const message = `Olá ${membro.nome}! 👋

Você foi convidado(a) para participar do grupo de testes comportamentais "${grupo.nome}".

📋 *Testes a realizar:* ${tiposLabel}
⏰ *Prazo:* ${formatDate(grupo.expires_at)}
${linksSection ? `\n*Acesse pelo(s) link(s):*\n${linksSection}\n` : ''}
Qualquer dúvida, estamos à disposição!`;
    
    return encodeURIComponent(message);
  };

  // Enviar convite individual via WhatsApp
  const handleSendSingleWhatsApp = (membro, grupo) => {
    const phone = normalizePhone(membro.telefone);
    if (!phone) {
      toast.error('Telefone inválido ou não informado');
      return;
    }
    
    const message = buildWhatsAppMessage(membro, grupo);
    const whatsappUrl = `https://wa.me/${phone}?text=${message}`;
    
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    toast.success(`Abrindo WhatsApp para ${membro.nome}`);
  };

  // Enviar convites em lote via WhatsApp (abre para cada membro pendente)
  const handleSendWhatsAppInvites = (grupo) => {
    const membrosPendentes = grupo.membros?.filter(m => m.telefone && !m.completo) || [];
    
    if (membrosPendentes.length === 0) {
      toast.info('Não há membros pendentes com WhatsApp cadastrado');
      return;
    }
    
    // Abre WhatsApp para o primeiro membro pendente
    const primeiro = membrosPendentes[0];
    handleSendSingleWhatsApp(primeiro, grupo);
    
    if (membrosPendentes.length > 1) {
      toast.info(`${membrosPendentes.length} membros pendentes. Clique no ícone de cada um para enviar individualmente.`);
    }
  };

  // ==================== GERENCIAR MEMBROS ====================

  // Abrir modal de edição de membros
  const handleOpenEditMembers = () => {
    setShowEditMembersModal(true);
    setNewMemberForGroup({ cpf: '', nome: '', email: '', telefone: '', cargo: '' });
    setEditingMember(null);
  };

  // Adicionar novo membro ao grupo existente
  const handleAddMemberToGroup = async () => {
    if (!groupDetail) return;
    
    const cpfLimpo = cleanCPF(newMemberForGroup.cpf);
    if (!cpfLimpo || cpfLimpo.length !== 11) {
      toast.error('CPF inválido');
      return;
    }
    
    if (!newMemberForGroup.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    
    setSavingMember(true);
    try {
      await testGroupsAPI.addMember(groupDetail.id, {
        cpf: cpfLimpo,
        nome: newMemberForGroup.nome,
        email: newMemberForGroup.email || null,
        telefone: newMemberForGroup.telefone || null,
        cargo: newMemberForGroup.cargo || null
      });
      
      toast.success('Membro adicionado com sucesso!');
      setNewMemberForGroup({ cpf: '', nome: '', email: '', telefone: '', cargo: '' });
      
      // Recarregar detalhes do grupo
      const response = await testGroupsAPI.getById(groupDetail.id);
      setGroupDetail(response.data);
      fetchGrupos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar membro');
    } finally {
      setSavingMember(false);
    }
  };

  // Iniciar edição de membro
  const handleStartEditMember = (membro) => {
    setEditingMember({
      cpf: membro.cpf,
      nome: membro.nome,
      email: membro.email || '',
      telefone: membro.telefone || '',
      cargo: membro.cargo || ''
    });
  };

  // Salvar edição de membro
  const handleSaveEditMember = async () => {
    if (!groupDetail || !editingMember) return;
    
    setSavingMember(true);
    try {
      await testGroupsAPI.updateMember(groupDetail.id, editingMember.cpf, {
        cpf: editingMember.cpf,
        nome: editingMember.nome,
        email: editingMember.email || null,
        telefone: editingMember.telefone || null,
        cargo: editingMember.cargo || null
      });
      
      toast.success('Membro atualizado com sucesso!');
      setEditingMember(null);
      
      // Recarregar detalhes do grupo
      const response = await testGroupsAPI.getById(groupDetail.id);
      setGroupDetail(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar membro');
    } finally {
      setSavingMember(false);
    }
  };

  // Remover membro do grupo
  const handleRemoveMember = async (cpf) => {
    if (!groupDetail) return;
    
    if (!window.confirm('Tem certeza que deseja remover este membro? Os convites pendentes serão cancelados.')) {
      return;
    }
    
    try {
      await testGroupsAPI.removeMember(groupDetail.id, cpf);
      toast.success('Membro removido com sucesso!');
      
      // Recarregar detalhes do grupo
      const response = await testGroupsAPI.getById(groupDetail.id);
      setGroupDetail(response.data);
      fetchGrupos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover membro');
    }
  };

  // Status badge
  const getStatusBadge = (status, progresso) => {
    if (status === 'arquivado') {
      return <Badge variant="outline" className="text-slate-500">Arquivado</Badge>;
    }
    if (status === 'expirado') {
      return <Badge variant="outline" className="text-red-500">Expirado</Badge>;
    }
    if (progresso === 100) {
      return <Badge className="bg-green-100 text-green-700">Completo</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-700">Em Andamento</Badge>;
  };

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="grupos-testes-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-7 h-7 text-indigo-600" />
              Grupos de Testes
            </h1>
            <p className="text-slate-500 mt-1">
              Aplique testes comportamentais em grupos de trabalhadores
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchGrupos}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
            <Button onClick={() => setShowCreateModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              Novo Grupo
            </Button>
          </div>
        </div>

        {/* Lista de Grupos */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : grupos.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">Nenhum grupo de testes criado</p>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Grupo
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {grupos.filter(g => g.status !== 'arquivado').map((grupo) => (
              <Card 
                key={grupo.id} 
                className="hover:border-indigo-300 transition-colors cursor-pointer"
                onClick={() => handleOpenDetail(grupo)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg text-slate-900">{grupo.nome}</h3>
                        {getStatusBadge(grupo.status, grupo.progresso)}
                      </div>
                      
                      {grupo.descricao && (
                        <p className="text-sm text-slate-500 mb-3">{grupo.descricao}</p>
                      )}
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        {grupo.tipos_teste.map(tipo => (
                          <Badge key={tipo} className={TIPOS_TESTE[tipo]?.color}>
                            {TIPOS_TESTE[tipo]?.icon} {TIPOS_TESTE[tipo]?.label}
                          </Badge>
                        ))}
                      </div>
                      
                      <div className="flex items-center gap-6 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {grupo.respondidos}/{grupo.total_convites} respostas
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Expira: {formatDate(grupo.expires_at)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Barra de progresso */}
                      <div className="w-32 hidden sm:block">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-slate-500">Progresso</span>
                          <span className="font-medium">{grupo.progresso}%</span>
                        </div>
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-indigo-600 transition-all duration-500"
                            style={{ width: `${grupo.progresso}%` }}
                          />
                        </div>
                      </div>
                      
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Modal de Criação */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                Criar Grupo de Testes
              </DialogTitle>
              <DialogDescription>
                Crie um grupo e envie convites de teste para múltiplos trabalhadores
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Informações do Grupo */}
              <div className="grid gap-4">
                <div>
                  <Label>Nome do Grupo *</Label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Ex: Equipe Comercial - Avaliação Q1"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Descrição (opcional)</Label>
                  <Textarea
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    placeholder="Descrição ou objetivo deste grupo de testes"
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </div>

              {/* Tipos de Teste */}
              <div>
                <Label className="mb-3 block">Tipos de Teste *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {Object.entries(TIPOS_TESTE).map(([key, config]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleTipoTeste(key)}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        formData.tipos_teste.includes(key)
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">{config.icon}</div>
                      <div className="font-medium text-sm">{config.label}</div>
                      {formData.tipos_teste.includes(key) && (
                        <CheckCircle className="w-4 h-4 text-indigo-600 absolute top-2 right-2" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Configurações */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Validade (dias)</Label>
                  <Select
                    value={String(formData.validade_dias)}
                    onValueChange={(val) => setFormData({ ...formData, validade_dias: parseInt(val) })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 dias</SelectItem>
                      <SelectItem value="14">14 dias</SelectItem>
                      <SelectItem value="30">30 dias</SelectItem>
                      <SelectItem value="60">60 dias</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Psicólogo Responsável</Label>
                  <Select
                    value={formData.psicologo_id || 'none'}
                    onValueChange={(val) => setFormData({ ...formData, psicologo_id: val === 'none' ? '' : val })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Selecionar (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {psychologists.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.nome} {p.crp ? `(CRP: ${p.crp})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Membros do Grupo */}
              <div>
                <Label className="mb-3 block">Membros do Grupo ({membros.length})</Label>
                
                {/* Formulário de adição */}
                <div className="grid grid-cols-12 gap-2 mb-3">
                  <Input
                    placeholder="CPF *"
                    value={novoMembro.cpf}
                    onChange={(e) => setNovoMembro({ ...novoMembro, cpf: e.target.value })}
                    className="col-span-2"
                  />
                  <Input
                    placeholder="Nome *"
                    value={novoMembro.nome}
                    onChange={(e) => setNovoMembro({ ...novoMembro, nome: e.target.value })}
                    className="col-span-3"
                  />
                  <Input
                    placeholder="Email"
                    value={novoMembro.email}
                    onChange={(e) => setNovoMembro({ ...novoMembro, email: e.target.value })}
                    className="col-span-3"
                  />
                  <Input
                    placeholder="WhatsApp"
                    value={novoMembro.telefone}
                    onChange={(e) => setNovoMembro({ ...novoMembro, telefone: e.target.value })}
                    className="col-span-2"
                  />
                  <Button 
                    type="button" 
                    onClick={handleAddMembro}
                    className="col-span-2"
                    variant="outline"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Adicionar
                  </Button>
                </div>
                
                {/* Lista de membros */}
                {membros.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>CPF</TableHead>
                          <TableHead>Nome</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>WhatsApp</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {membros.map((membro, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-sm">{formatCPF(membro.cpf)}</TableCell>
                            <TableCell>{membro.nome}</TableCell>
                            <TableCell className="text-slate-500">{membro.email || '-'}</TableCell>
                            <TableCell className="text-slate-500">{membro.telefone || '-'}</TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveMembro(idx)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="border-2 border-dashed rounded-lg p-8 text-center text-slate-400">
                    Adicione membros ao grupo usando o formulário acima
                  </div>
                )}
                
                {membros.length > 0 && formData.tipos_teste.length > 0 && (
                  <p className="text-sm text-slate-500 mt-2">
                    Serão enviados <strong>{membros.length * formData.tipos_teste.length}</strong> convites 
                    ({membros.length} membros × {formData.tipos_teste.length} teste(s))
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                Cancelar
              </Button>
              <Button 
                onClick={handleCreateGroup} 
                disabled={creating}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Criar e Enviar Convites
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Detalhes do Grupo */}
        <Dialog open={!!selectedGroup} onOpenChange={() => { setSelectedGroup(null); setGroupDetail(null); }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            {loadingDetail ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : groupDetail ? (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-indigo-600" />
                    {groupDetail.nome}
                  </DialogTitle>
                  <DialogDescription>
                    {groupDetail.descricao || 'Dashboard de resultados do grupo'}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                  {/* Estatísticas */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl font-bold text-slate-900">{groupDetail.stats?.total_membros}</div>
                        <div className="text-sm text-slate-500">Membros</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl font-bold text-green-600">{groupDetail.stats?.membros_completos}</div>
                        <div className="text-sm text-slate-500">Completos</div>
                      </CardContent>
                    </Card>
                    {groupDetail.tipos_teste.map(tipo => (
                      <Card key={tipo}>
                        <CardContent className="p-4 text-center">
                          <div className="text-3xl font-bold text-indigo-600">
                            {groupDetail.stats?.por_teste?.[tipo]?.respondidos || 0}
                            <span className="text-lg text-slate-400">/{groupDetail.stats?.por_teste?.[tipo]?.total || 0}</span>
                          </div>
                          <div className="text-sm text-slate-500">{TIPOS_TESTE[tipo]?.label}</div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Médias do Grupo (DISC/G2BP) */}
                  {(groupDetail.medias?.disc && Object.keys(groupDetail.medias.disc).length > 0) || 
                   (groupDetail.medias?.g2bp && Object.keys(groupDetail.medias.g2bp).length > 0) ? (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Perfil Médio do Grupo</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          {groupDetail.tipos_teste.includes('disc') && groupDetail.medias?.disc && Object.keys(groupDetail.medias.disc).length > 0 && (
                            <div>
                              <p className="text-sm font-medium text-slate-500 mb-3">DISC Tradicional</p>
                              <div className="space-y-2">
                                {['D', 'I', 'S', 'C'].map(letra => (
                                  <div key={letra} className="flex items-center gap-3">
                                    <span className="w-8 font-bold text-slate-700">{letra}</span>
                                    <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                                      <div 
                                        className={`h-full transition-all duration-500 ${
                                          letra === 'D' ? 'bg-red-500' :
                                          letra === 'I' ? 'bg-amber-500' :
                                          letra === 'S' ? 'bg-green-500' : 'bg-blue-500'
                                        }`}
                                        style={{ width: `${groupDetail.medias.disc[letra] || 0}%` }}
                                      />
                                    </div>
                                    <span className="w-12 text-right font-medium">{groupDetail.medias.disc[letra] || 0}%</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {groupDetail.tipos_teste.includes('g2bp') && groupDetail.medias?.g2bp && Object.keys(groupDetail.medias.g2bp).length > 0 && (
                            <div>
                              <p className="text-sm font-medium text-slate-500 mb-3">G2 Behavioral Profile</p>
                              <div className="space-y-2">
                                {['D', 'I', 'S', 'C'].map(letra => (
                                  <div key={letra} className="flex items-center gap-3">
                                    <span className="w-8 font-bold text-slate-700">{letra}</span>
                                    <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                                      <div 
                                        className={`h-full transition-all duration-500 ${
                                          letra === 'D' ? 'bg-red-500' :
                                          letra === 'I' ? 'bg-amber-500' :
                                          letra === 'S' ? 'bg-green-500' : 'bg-blue-500'
                                        }`}
                                        style={{ width: `${groupDetail.medias.g2bp[letra] || 0}%` }}
                                      />
                                    </div>
                                    <span className="w-12 text-right font-medium">{groupDetail.medias.g2bp[letra] || 0}%</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ) : null}

                  {/* Lista de Membros */}
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="text-lg">Membros ({groupDetail.membros?.length})</CardTitle>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={handleOpenEditMembers}
                          className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                          data-testid="edit-members-btn"
                        >
                          <Pencil className="w-4 h-4 mr-2" />
                          Gerenciar Membros
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleSendWhatsAppInvites(groupDetail)}
                          className="text-green-600 border-green-300 hover:bg-green-50"
                          data-testid="send-whatsapp-invites-btn"
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Enviar Convites via WhatsApp
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Nome</TableHead>
                            <TableHead>CPF</TableHead>
                            <TableHead>WhatsApp</TableHead>
                            {groupDetail.tipos_teste.map(tipo => (
                              <TableHead key={tipo} className="text-center">{TIPOS_TESTE[tipo]?.icon}</TableHead>
                            ))}
                            <TableHead className="text-center">Status</TableHead>
                            <TableHead className="text-center">Ações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {groupDetail.membros?.map((membro, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{membro.nome}</TableCell>
                              <TableCell className="font-mono text-sm">{formatCPF(membro.cpf)}</TableCell>
                              <TableCell className="text-slate-500">{membro.telefone || '-'}</TableCell>
                              {groupDetail.tipos_teste.map(tipo => {
                                const teste = membro.testes?.[tipo];
                                return (
                                  <TableCell key={tipo} className="text-center">
                                    {teste?.status === 'respondido' ? (
                                      <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                                    ) : (
                                      <Clock className="w-5 h-5 text-amber-500 mx-auto" />
                                    )}
                                  </TableCell>
                                );
                              })}
                              <TableCell className="text-center">
                                {membro.completo ? (
                                  <Badge className="bg-green-100 text-green-700">Completo</Badge>
                                ) : (
                                  <Badge className="bg-amber-100 text-amber-700">Pendente</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {membro.telefone && !membro.completo && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleSendSingleWhatsApp(membro, groupDetail)}
                                    className="text-green-600 hover:text-green-800"
                                    title="Enviar convite via WhatsApp"
                                    data-testid={`send-whatsapp-${membro.cpf}`}
                                  >
                                    <MessageSquare className="w-4 h-4" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>

                <DialogFooter className="flex justify-between">
                  <Button 
                    variant="outline" 
                    onClick={() => handleDeleteGroup(groupDetail.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Arquivar
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => navigate(`/company/grupos-testes/${groupDetail.id}/relatorio`)}>
                      <Download className="w-4 h-4 mr-2" />
                      Relatório PDF
                    </Button>
                    <Button onClick={() => { setSelectedGroup(null); setGroupDetail(null); }}>
                      Fechar
                    </Button>
                  </div>
                </DialogFooter>
              </>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Modal de Gerenciar Membros */}
        <Dialog open={showEditMembersModal} onOpenChange={setShowEditMembersModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                Gerenciar Membros - {groupDetail?.nome}
              </DialogTitle>
              <DialogDescription>
                Adicione, edite ou remova participantes do grupo
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Adicionar novo membro */}
              <div className="p-4 bg-slate-50 rounded-lg border">
                <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  Adicionar Novo Membro
                </h4>
                <div className="grid grid-cols-12 gap-2">
                  <Input
                    placeholder="CPF *"
                    value={newMemberForGroup.cpf}
                    onChange={(e) => setNewMemberForGroup({ ...newMemberForGroup, cpf: e.target.value })}
                    className="col-span-3"
                    data-testid="new-member-cpf"
                  />
                  <Input
                    placeholder="Nome *"
                    value={newMemberForGroup.nome}
                    onChange={(e) => setNewMemberForGroup({ ...newMemberForGroup, nome: e.target.value })}
                    className="col-span-3"
                    data-testid="new-member-nome"
                  />
                  <Input
                    placeholder="Email"
                    value={newMemberForGroup.email}
                    onChange={(e) => setNewMemberForGroup({ ...newMemberForGroup, email: e.target.value })}
                    className="col-span-3"
                    data-testid="new-member-email"
                  />
                  <Input
                    placeholder="WhatsApp"
                    value={newMemberForGroup.telefone}
                    onChange={(e) => setNewMemberForGroup({ ...newMemberForGroup, telefone: e.target.value })}
                    className="col-span-2"
                    data-testid="new-member-telefone"
                  />
                  <Button 
                    onClick={handleAddMemberToGroup}
                    disabled={savingMember}
                    className="col-span-1 bg-indigo-600 hover:bg-indigo-700"
                    data-testid="add-member-btn"
                  >
                    {savingMember ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Lista de membros */}
              <div>
                <h4 className="font-medium text-slate-700 mb-3">
                  Membros Atuais ({groupDetail?.membros?.length || 0})
                </h4>
                <div className="space-y-2">
                  {groupDetail?.membros?.map((membro, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-lg border ${editingMember?.cpf === membro.cpf ? 'bg-indigo-50 border-indigo-300' : 'bg-white'}`}
                    >
                      {editingMember?.cpf === membro.cpf ? (
                        // Modo edição
                        <div className="space-y-2">
                          <div className="grid grid-cols-12 gap-2">
                            <Input
                              value={editingMember.nome}
                              onChange={(e) => setEditingMember({ ...editingMember, nome: e.target.value })}
                              placeholder="Nome"
                              className="col-span-3"
                            />
                            <Input
                              value={editingMember.email}
                              onChange={(e) => setEditingMember({ ...editingMember, email: e.target.value })}
                              placeholder="Email"
                              className="col-span-3"
                            />
                            <Input
                              value={editingMember.telefone}
                              onChange={(e) => setEditingMember({ ...editingMember, telefone: e.target.value })}
                              placeholder="WhatsApp"
                              className="col-span-3"
                            />
                            <Input
                              value={editingMember.cargo}
                              onChange={(e) => setEditingMember({ ...editingMember, cargo: e.target.value })}
                              placeholder="Cargo"
                              className="col-span-3"
                            />
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingMember(null)}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Cancelar
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleSaveEditMember}
                              disabled={savingMember}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              {savingMember ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                              Salvar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // Modo visualização
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <span className="font-medium">{membro.nome}</span>
                              <span className="text-sm text-slate-500 font-mono">{formatCPF(membro.cpf)}</span>
                              {membro.completo && (
                                <Badge className="bg-green-100 text-green-700 text-xs">Completo</Badge>
                              )}
                            </div>
                            <div className="text-sm text-slate-500 mt-1">
                              {membro.email && <span className="mr-3">{membro.email}</span>}
                              {membro.telefone && <span className="mr-3">📱 {membro.telefone}</span>}
                              {membro.cargo && <span>• {membro.cargo}</span>}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStartEditMember(membro)}
                              className="text-indigo-600 hover:text-indigo-800"
                              title="Editar"
                              data-testid={`edit-member-${membro.cpf}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {!membro.completo && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveMember(membro.cpf)}
                                className="text-red-500 hover:text-red-700"
                                title="Remover"
                                data-testid={`remove-member-${membro.cpf}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={() => setShowEditMembersModal(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
